import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import { Form } from 'formik';
import { SnapshotFrom, EventFrom } from 'xstate';
import { WysiwygField } from '@uncinc/uncinc-react-kitchen-sink';
import { FormCheckbox, Callout, FormikWrapper } from '@hvcgroep/design-system';
import { RegistrationDeskContext, registrationDeskMachine } from '../../state-machine';
import { APP_ID, WIDGET_OPTIONS } from '../../../../shared/config/constants';
import ConfirmBlock from '../../containers/ConfirmBlock';
import StickyConfirmRegistrationButton from '../../components/StickyConfirmRegistrationButton';
import { ContractApplicationParams } from '../../types/orchestrator';
import { RegistrationDeskStepComponent } from '../../types/multistep-form';
import JourneyActionButtons from '../../components/JourneyActionButtons';
import { heatApi, usePostContractApplicationMutation } from '../../store/slices/HeatApi';
import gtm from "../../../../shared/helpers/gtm";
import trackValidationError from '../../../../shared/helpers/trackValidationError';

type Props = {
  snapshot: SnapshotFrom<typeof registrationDeskMachine>;
  send: (event: EventFrom<typeof registrationDeskMachine>) => void,
  onGoBack: () => void;
};

const ConfirmScreen: RegistrationDeskStepComponent<'proceed.acceptTermsAndConditions', Props> = (props) => {
  const { snapshot, send, onGoBack } = props;
  const { t } = useTranslation();
  const [postContractApplication, {
    isError,
  }] = usePostContractApplicationMutation();
  const { data: offer } = heatApi.endpoints.getOffer.useQuery({ ean: snapshot.context.eamData?.Ean!, sjvGJ: snapshot.context.usageGJ });

  const buttonRef = useRef<HTMLButtonElement>(null);

  return (
    <div className="ConfirmScreen">
      {isError && (
        <Callout className="u-margin--bottom--7">
          <h3 className="h5">{t('registration_desk.screens.confirm.error.heading')}</h3>
          <p>
            {t('registration_desk.screens.confirm.error.content')}
          </p>
        </Callout>
      )}
      <div className="u-margin--bottom--7">
        <ConfirmBlock
          snapshot={snapshot}
          send={send}
        />
      </div>
      <h4>{t('registration_desk.screens.confirm.terms.title')}</h4>
      <div className="u-margin--bottom--4">
        <WysiwygField
          html={WIDGET_OPTIONS?.registrationDeskOptions?.termsAndConditions}
          // @ts-ignore
          customElementHandler={(element) => {
            if (element.name === 'a') {
              const label = element.children[0].data;
              const link = element.attribs.href;

              return (
                <a
                  href={link}
                  target="_blank"
                  rel="noreferrer"
                  onClick={
                    (e) => {
                      gtm.trackUserEvent(String(APP_ID), 'Open terms document', label);
                    }
                  }
                >
                  {label}
                </a>
              );
            }
            return null;
          }}
        />
      </div>
      <FormikWrapper
        initialValues={{
          accepted_terms: false,
        }}
        validationSchema={Yup.object()
          .shape({
            accepted_terms: Yup.bool()
              .isTrue(t('registration_desk.screens.confirm.terms.error.required')),
          })}
        validateOnBlur={false}
        validateOnChange={false}
        onSubmit={async (_, helpers) => {
          const values = assertContextNullValues(snapshot.context);
          const parsedValues = parseSubmittedValues(values, snapshot.context.usageGJ!, offer?.MonthAmount!);

          const result = await postContractApplication(parsedValues);

          if ('data' in result) {
            window.location.replace(WIDGET_OPTIONS?.registrationDeskOptions?.thanksUrl!);
          }

          helpers.setSubmitting(false);
        }}
        onValidationError={(errors) => {
          trackValidationError(String(APP_ID), 'Confirm', errors);
        }}
      >
        {({
          isSubmitting,
        }) => {
          return (
            <Form>
              <FormCheckbox
                name="accepted_terms"
                label={t('registration_desk.screens.confirm.terms.label')}
                isLiveValidationDisabled
              />
              <Callout className="mt-5">
                {t('registration_desk.screens.confirm.callout')}
              </Callout>
              <JourneyActionButtons
                onGoBack={onGoBack}
                ref={buttonRef}
                submitButtonType="primary"
                submitButtonChildren={t('registration_desk.screens.confirm.submit')}
                submitButtonIsLoading={isSubmitting}
              />
              <StickyConfirmRegistrationButton buttonRef={buttonRef} isSubmitting={isSubmitting} />
            </Form>
          );
        }}
      </FormikWrapper>
    </div>
  );
};

function assertContextNullValues(context: RegistrationDeskContext) {
  return {
    registrationType: context.registrationType!,
    eamData: context.eamData!,
    usage: context.usageGJ!,
    salutation: context.salutation!,
    firstName: context.firstName!,
    infix: context.infix,
    lastName: context.lastName!,
    dateOfBirth: context.dateOfBirth!,
    email: context.email!,
    tel: context.tel!,
    billingAddress: context.billingAddress,
    startDate: context.startDate!,
    paymentMethod: context.paymentMethod!,
    iban: context.iban!,
  };
}

function parseSubmittedValues(values: ReturnType<typeof assertContextNullValues>, sjv: string, offer: string): ContractApplicationParams {
  function formatDate(date: Date) {
    return date.toISOString().split('T')[0].replace(/-/g, '');
  }

  return {
    CPName: `${values.infix ? `${values.infix} ` : ''}${values.lastName}`,
    CPFirstName: values.firstName,
    CPCustomerType: 'RES',
    CPAddress1: values.billingAddress ? `${values.billingAddress.huisnummer}${values.billingAddress.huisnummertoevoeging ?? ''}` : `${values.eamData.Housenumber}${values.eamData.HousenumberExt ?? ''}`,
    CPAddress2: values.billingAddress ? values.billingAddress.straatnaam : values.eamData.Street,
    CPPostcalCode: values.billingAddress ? String(values.billingAddress.postcode) : values.eamData.PostalCode,
    CPCity: values.billingAddress ? values.billingAddress.woonplaatsnaam : values.eamData.City,
    EANcode: values.eamData.Ean,
    EANAddress1: `${values.eamData.Housenumber}${values.eamData.HousenumberExt ?? ''}`,
    EANAddress2: values.eamData.Street,
    EANPostalCode: values.eamData.PostalCode,
    EANCity: values.eamData.City,
    ContractSwitchDate: formatDate(values.startDate),
    IBANUDF: values.iban.replace(/\s/g, ''),
    PaymentMethodUDF: values.paymentMethod === 'directDebit' ? 'AUT' : 'IDEAL',
    ContactPrefix: values.salutation === 'madam' ? 'mevrouw' : 'meneer',
    ContactName: `${values.infix ? ' ' : ''}${values.lastName}`,
    ContactFirstName: values.firstName,
    ContactEmail: values.email,
    ContactPhoneNumber: values.tel,
    ContactAddress1: `${values.eamData.Housenumber}${values.eamData.HousenumberExt ?? ''}`,
    ContactAddress2: values.eamData.Street,
    ContactPostalCode: values.eamData.PostalCode,
    ContactCity: values.eamData.City,
    MeterNo: values.eamData.MeterNo,
    EANLoadZone: values.eamData.LoadZone,
    EANCaptarUDF: values.eamData.CaptarUDF,
    EANVartarUDF: values.eamData.VartarUDF,
    EANCustomerTypeUDF: 'KV',
    EANTapCapacityUDF: values.eamData.TapCapacityUDF,
    EANMaxVolumeNoUDF: values.eamData.MaxVolumeNoUDF,
    BirthDay: formatDate(values.dateOfBirth),
    EANHeatMeterType: values.eamData.CommModule,
    EANSignUpType: values.registrationType === 'moving' ? 'VERH' : 'RENO',
    EANOfferAmount: offer,
    EANSjvTotUDF: sjv,
  };
}

export default ConfirmScreen;
